import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, AlertCircle, CheckCircle, X, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Spinner } from '@centraoneplus/ui';
import { customerService } from '@services/customer.service';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { ThemedSelect } from '@components/common/ThemedSelect';

// UTF-8 safe base64 encoder. Plain btoa() throws on any non-Latin1 character
// (accents, smart quotes, em-dashes) which are common in real CSV/Excel exports.
const encodeBase64Utf8 = (str: string): string => {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

interface ImportDuplicate {
  row: number;
  email?: string;
  phone?: string;
  reasons: string[];
}

interface PreviewData {
  headers: string[];
  sampleRows: Record<string, string>[];
  totalRows: number;
  suggestedMappings: { sourceColumn: string; targetField: string }[];
  duplicates?: ImportDuplicate[];
  duplicateCount?: number;
}

interface FieldMapping {
  sourceColumn: string;
  targetField: string;
}

const CUSTOMER_FIELDS = [
  { value: '', label: 'Skip this field' },
  { value: 'email', label: 'Email (required)' },
  { value: 'name', label: 'Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'externalId', label: 'External ID' },
  { value: 'source', label: 'Source' },
  { value: 'timezone', label: 'Timezone' },
  { value: 'locale', label: 'Locale' },
];

export default function ImportCustomersPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<'upload' | 'mapping' | 'importing' | 'complete'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  // Effective format sent to the importer. Excel files are parsed in the browser
  // and converted to JSON rows, so the backend only ever sees 'csv' or 'json'.
  const [fileFormat, setFileFormat] = useState<'csv' | 'json'>('csv');
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [importResult, setImportResult] = useState<{
    jobId: string;
    status: string;
    successCount: number;
    errorCount: number;
    errors: { row?: number; message: string }[];
  } | null>(null);

  const [importOptions, setImportOptions] = useState({
    updateExisting: false,
    skipErrors: true,
    tags: [] as string[],
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const name = selectedFile.name.toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    const isCsv = name.endsWith('.csv');
    const isJson = name.endsWith('.json');

    // Validate file type
    if (!isExcel && !isCsv && !isJson) {
      setError('Please upload a CSV, Excel (.xlsx/.xls) or JSON file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setIsLoading(true);

    try {
      let content: string;
      let format: 'csv' | 'json';

      if (isExcel) {
        // Parse the Excel workbook in the browser and convert the first sheet to
        // JSON rows, then reuse the existing JSON import path on the backend.
        const buffer = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const rows = sheetName
          ? XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '', raw: false })
          : [];
        if (rows.length === 0) {
          setError('The Excel file has no data rows.');
          setIsLoading(false);
          return;
        }
        content = JSON.stringify(rows);
        format = 'json';
      } else {
        content = await selectedFile.text();
        format = isJson ? 'json' : 'csv';
      }

      setFileContent(content);
      setFileFormat(format);

      const previewData = await customerService.previewImport({
        fileContent: encodeBase64Utf8(content),
        format,
        previewRows: 5,
      });
      setPreview(previewData);

      // Initialize mappings from suggestions
      const initialMappings: FieldMapping[] = previewData.headers.map((header) => {
        const suggestion = previewData.suggestedMappings?.find(
          (m) => m.sourceColumn === header
        );
        return {
          sourceColumn: header,
          targetField: suggestion?.targetField || '',
        };
      });
      setMappings(initialMappings);
      setStep('mapping');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to read or preview the file');
    } finally {
      setIsLoading(false);
    }
  };

  // Sample values for each importable field, used to build the downloadable template.
  const TEMPLATE_SAMPLE: Record<string, string> = {
    email: 'jane.doe@example.com',
    name: 'Jane Doe',
    phone: '5551234567',
    externalId: 'EXT-1001',
    source: 'manual',
    timezone: 'America/New_York',
    locale: 'en-US',
  };

  const handleDownloadTemplate = (format: 'csv' | 'json' | 'xlsx') => {
    const fields = CUSTOMER_FIELDS.filter((f) => f.value).map((f) => f.value);
    const sample: Record<string, string> = {};
    fields.forEach((field) => {
      sample[field] = TEMPLATE_SAMPLE[field] ?? '';
    });

    // Excel is written as a real .xlsx workbook via SheetJS.
    if (format === 'xlsx') {
      const worksheet = XLSX.utils.json_to_sheet([sample], { header: fields });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
      XLSX.writeFile(workbook, 'customer-import-template.xlsx');
      return;
    }

    let content: string;
    let mimeType: string;
    if (format === 'json') {
      content = JSON.stringify([sample], null, 2);
      mimeType = 'application/json';
    } else {
      const header = fields.join(',');
      const sampleRow = fields.map((field) => TEMPLATE_SAMPLE[field] ?? '').join(',');
      content = `${header}\n${sampleRow}`;
      mimeType = 'text/csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-import-template.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleMappingChange = (sourceColumn: string, targetField: string) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.sourceColumn === sourceColumn ? { ...m, targetField } : m
      )
    );
  };

  const handleImport = async () => {
    // Validate that email is mapped
    const emailMapping = mappings.find((m) => m.targetField === 'email');
    if (!emailMapping) {
      setError('Email field mapping is required');
      return;
    }

    setStep('importing');
    setError(null);

    try {
      const result = await customerService.startImport({
        fileName: file?.name || 'import.csv',
        fileContent: encodeBase64Utf8(fileContent),
        format: fileFormat,
        mappings: mappings.filter((m) => m.targetField),
        updateExisting: importOptions.updateExisting,
        skipErrors: importOptions.skipErrors,
        tags: importOptions.tags,
      });

      // Poll for completion
      let jobStatus = result;
      while (jobStatus.status === 'pending' || jobStatus.status === 'processing') {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        jobStatus = await customerService.getImportJob(result.jobId);
      }

      setImportResult({
        jobId: jobStatus.jobId,
        status: jobStatus.status,
        successCount: jobStatus.successCount,
        errorCount: jobStatus.errorCount,
        errors: jobStatus.errors || [],
      });
      setStep('complete');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Import failed');
      setStep('mapping');
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-6">
      {/* Header — floating card, row 1 only */}
      <div
        className="flex items-center justify-between gap-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-[18px] py-4"
        style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
      >
        <div className="flex min-w-0 items-center gap-4">
        <Link
          to="/customers"
          className="p-2 hover:bg-[var(--co-selected)] rounded-[10px] transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-[var(--co-ink-3)]" />
        </Link>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-[var(--co-ink)]">Import Customers</h1>
          <p className="mt-1 text-sm text-[var(--co-ink-3)]">
            Upload a CSV or JSON file to import customers
          </p>
        </div>
        </div>
        <div className="flex h-[34px] flex-none items-center">
          <PageHeaderMeta />
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4">
        {['Upload', 'Map Fields', 'Import'].map((label, idx) => {
          const stepNum = idx + 1;
          const currentStepNum = step === 'upload' ? 1 : step === 'mapping' ? 2 : 3;
          const isActive = stepNum === currentStepNum;
          const isComplete = stepNum < currentStepNum;

          return (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isComplete
                    ? 'bg-green-500 text-white'
                    : isActive
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                {isComplete ? <CheckCircle className="h-5 w-5" /> : stepNum}
              </div>
              <span
                className={`text-sm ${
                  isActive ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {label}
              </span>
              {idx < 2 && (
                <div className="w-12 h-px bg-gray-200 dark:bg-gray-700 mx-2" />
              )}
            </div>
          );
        })}
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4 text-red-500" />
          </button>
        </div>
      )}

      {/* Upload Step */}
      {step === 'upload' && (
        <div className="card p-8">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,.xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center cursor-pointer hover:border-primary-500 transition-colors"
          >
            {isLoading ? (
              <div className="flex flex-col items-center gap-4">
                <Spinner size="lg" />
                <p className="text-gray-500 dark:text-gray-400">Processing file...</p>
              </div>
            ) : (
              <>
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
                <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
                  Drop your file here, or click to browse
                </p>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Supports CSV, Excel (.xlsx/.xls) and JSON files
                </p>
              </>
            )}
          </div>

          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">
              File Format Requirements
            </h3>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>- CSV: First row should contain column headers</li>
              <li>- Excel (.xlsx/.xls): First row of the first sheet should contain column headers</li>
              <li>- JSON: Array of objects with consistent keys</li>
              <li>- Email field is required for each customer</li>
              <li>- Maximum file size: 10MB</li>
            </ul>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Not sure how to format your file? Download a template to get started:
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleDownloadTemplate('csv')}
                  className="btn btn-outline btn-sm flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  CSV template
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadTemplate('xlsx')}
                  className="btn btn-outline btn-sm flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Excel template
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadTemplate('json')}
                  className="btn btn-outline btn-sm flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  JSON template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mapping Step */}
      {step === 'mapping' && preview && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Field Mapping
              </h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {preview.totalRows} rows found
              </span>
            </div>

            <div className="space-y-4">
              {mappings.map((mapping) => (
                <div key={mapping.sourceColumn} className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      <FileText className="h-4 w-4" />
                      {mapping.sourceColumn}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                      Sample: {preview.sampleRows[0]?.[mapping.sourceColumn] || '-'}
                    </div>
                  </div>
                  <div className="text-gray-400 dark:text-gray-500">→</div>
                  <div className="flex-1">
                    <ThemedSelect
                      value={mapping.targetField}
                      onChange={(e) =>
                        handleMappingChange(mapping.sourceColumn, e.target.value)
                      }
                      className="input w-full"
                    >
                      {CUSTOMER_FIELDS.map((field) => (
                        <option key={field.value} value={field.value}>
                          {field.label}
                        </option>
                      ))}
                    </ThemedSelect>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pre-commit duplicate flagging (US #8695 / TC #8846). Detected on the
              full file using the auto-detected email/phone columns so the user
              can review before importing. */}
          {preview.duplicateCount && preview.duplicateCount > 0 ? (
            <div className="card p-6 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-300">
                    {preview.duplicateCount} potential duplicate{preview.duplicateCount > 1 ? 's' : ''} found
                  </h2>
                  <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                    These rows match an existing customer or another row in this file. On import
                    they will be skipped (email) or rejected (phone) — review them before continuing.
                  </p>
                  <ul className="mt-3 space-y-1 text-sm text-amber-700 dark:text-amber-300 max-h-48 overflow-y-auto">
                    {preview.duplicates?.map((d) => (
                      <li key={d.row}>
                        <span className="font-medium">Row {d.row}</span>
                        {(d.email || d.phone) && (
                          <span> ({[d.email, d.phone].filter(Boolean).join(', ')})</span>
                        )}
                        : {d.reasons.join('; ')}
                      </li>
                    ))}
                  </ul>
                  {preview.duplicates && preview.duplicateCount > preview.duplicates.length && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      Showing the first {preview.duplicates.length} of {preview.duplicateCount}.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Import Options
            </h2>

            <div className="space-y-4">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={importOptions.updateExisting}
                  onChange={(e) =>
                    setImportOptions((prev) => ({
                      ...prev,
                      updateExisting: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
                />
                <span className="text-gray-700 dark:text-gray-300">
                  Update existing customers if email matches
                </span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={importOptions.skipErrors}
                  onChange={(e) =>
                    setImportOptions((prev) => ({
                      ...prev,
                      skipErrors: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
                />
                <span className="text-gray-700 dark:text-gray-300">
                  Skip rows with errors and continue importing
                </span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => {
                setStep('upload');
                setFile(null);
                setPreview(null);
                setMappings([]);
              }}
              className="btn btn-outline"
            >
              Back
            </button>
            <button onClick={handleImport} className="btn btn-primary">
              Start Import
            </button>
          </div>
        </div>
      )}

      {/* Importing Step */}
      {step === 'importing' && (
        <div className="card p-12 text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            Importing customers...
          </p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            This may take a few moments
          </p>
        </div>
      )}

      {/* Complete Step */}
      {step === 'complete' && importResult && (
        <div className="card p-8 text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Import Complete
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {importResult.successCount} customers imported successfully
            {importResult.errorCount > 0 && (
              <span className="text-orange-500">
                {' '}({importResult.errorCount} errors)
              </span>
            )}
          </p>
          {importResult.errors.length > 0 && (
            <div className="mb-6 text-left max-h-48 overflow-y-auto rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-4">
              <p className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-2">
                Some rows were not imported:
              </p>
              <ul className="space-y-1">
                {importResult.errors.map((e, idx) => (
                  <li key={idx} className="text-sm text-orange-700 dark:text-orange-300">
                    {e.row ? `Row ${e.row}: ` : ''}{e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => {
                setStep('upload');
                setFile(null);
                setPreview(null);
                setMappings([]);
                setImportResult(null);
              }}
              className="btn btn-outline"
            >
              Import More
            </button>
            <Link to="/customers" className="btn btn-primary">
              View Customers
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
